// =========================================================================
// IssueMachinePicture.transform — pure projections for the machine still.
// Fork-owned (zone C).
//
// The demo's `turnStill(f, health, comp)` (line 1828 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`) narrows the
// machine's condition register to the one component the alert or incident is
// about before it draws a callout:
//
//   const fl = health.filter(h => ATTN.includes(h.band) && (!comp || h.component === comp));
//
// `TurntableStill` and its `MachineIllustration` fallback already apply the
// band half of that filter (they only call out the attention bands), so this
// file does the component half — and turns the highlight into the words the thumbnail
// announces to a screen reader.
//
// Transformation layer: no React, no data access, no side effects.
// =========================================================================

import type { ComponentHealth } from '../../data/core';

/** One component name, several, or none (`null` / empty = the whole register). */
export type ThumbHighlight = string | readonly string[] | null | undefined;

/** Normalise the highlight prop to a de-duplicated list, blanks dropped. */
export function highlightList(highlight: ThumbHighlight): string[] {
  const raw = highlight == null ? [] : typeof highlight === 'string' ? [highlight] : highlight;
  return [...new Set(raw.map((c) => c.trim()).filter(Boolean))];
}

/**
 * The register rows the still draws: every row when nothing is highlighted,
 * otherwise only the highlighted components' rows — the component half of
 * demo line 1828's `fl` filter.
 */
export function thumbHealth(
  health: readonly ComponentHealth[],
  highlight: readonly string[],
): ComponentHealth[] {
  if (highlight.length === 0) return [...health];
  const want = new Set(highlight);
  return health.filter((h) => want.has(h.component));
}

/**
 * The accessible name for the still: machine, model, and which component is
 * highlighted with its condition score when the register has one.
 *
 * @example thumbAriaLabel('L-02', 'ML-21', ['Hydraulic pump & drive'], rows)
 *   // 'L-02 ML-21, side view. Highlighted: Hydraulic pump & drive, condition 38/100.'
 */
export function thumbAriaLabel(
  machineId: string,
  model: string,
  highlight: readonly string[],
  rows: readonly ComponentHealth[],
): string {
  const head = `${machineId} ${model}, side view.`;
  if (highlight.length === 0) return head;
  const parts = highlight.map((c) => {
    const row = rows.find((h) => h.component === c);
    return row ? `${c}, condition ${row.health_score}/100` : c;
  });
  return `${head} Highlighted: ${parts.join('; ')}.`;
}
